/* VZAEMOA: progressively enhanced Shopify storefront. No demo commerce data. */
(() => {
  const config = window.VZAEMOA || { root: '/', cart: '/cart' };
  const strings = config.strings || {};
  const controllers = new WeakMap();
  let cartBusy = false;
  let noticeTimer;
  let imageOpener;
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];
  const endpoint = name => config.root + 'cart/' + name + '.js';
  function message(node, text, error = false) {
    if (!node) return;
    node.textContent = text;
    node.classList.toggle('is-error', error);
  }
  async function request(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal });
      let data;
      try { data = await response.json(); } catch { throw new Error(strings.response_error); }
      if (!response.ok || data.status >= 400) throw new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : strings.cart_error);
      return data;
    } catch (error) {
      // A lost response may follow a successful mutation; never retry automatically.
      if (error.name === 'AbortError') throw new Error(strings.timeout_error);
      throw error;
    } finally { clearTimeout(timer); }
  }
  function showNotice(text) {
    const box = qs('#CartNotice');
    if (!box) return;
    message(qs('[data-notice-text]', box), text);
    box.hidden = false;
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => { box.hidden = true; }, 9000);
  }
  function setCartCount(count) { qsa('[data-cart-count]').forEach(node => { node.textContent = count; }); }
  function closeMenu() {
    qsa('[data-nav]').forEach(menu => { menu.open = false; });
    document.body.classList.remove('menu-open');
  }
  function initNavigation() {
    qsa('[data-nav]').forEach(menu => {
      if (menu.dataset.ready) return;
      menu.dataset.ready = 'true';
      menu.addEventListener('toggle', () => { document.body.classList.toggle('menu-open', menu.open); });
    });
  }
  function initGallery(gallery) {
    if (!gallery || gallery.dataset.ready) return;
    gallery.dataset.ready = 'true';
    const track = qs('[data-gallery-track]', gallery);
    const slides = [...track.children];
    const thumbs = qsa('[data-gallery-index]', gallery);
    const previous = qs('[data-gallery-prev]', gallery), next = qs('[data-gallery-next]', gallery);
    let current = 0;
    function update() {
      current = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / Math.max(track.clientWidth, 1))));
      message(qs('[data-gallery-status]', gallery), `${current + 1} / ${slides.length}`);
      previous.disabled = current === 0;
      next.disabled = current === slides.length - 1;
      thumbs.forEach((thumb, index) => thumb.setAttribute('aria-current', String(index === current)));
      slides.forEach((slide, index) => { if (index !== current) qsa('video', slide).forEach(video => video.pause()); });
    }
    gallery.goTo = (index, instant = false) => {
      const bounded = Math.max(0, Math.min(slides.length - 1, index));
      track.scrollTo({ left: bounded * track.clientWidth, behavior: instant || reducedMotion() ? 'instant' : 'smooth' });
      if (instant) update();
    };
    previous.addEventListener('click', () => gallery.goTo(current - 1));
    next.addEventListener('click', () => gallery.goTo(current + 1));
    thumbs.forEach(thumb => thumb.addEventListener('click', () => gallery.goTo(Number(thumb.dataset.galleryIndex))));
    track.addEventListener('scroll', update, { passive: true });
    track.addEventListener('keydown', event => {
      if (event.target !== track) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); gallery.goTo(current + (event.key === 'ArrowRight' ? 1 : -1)); }
    });
    new ResizeObserver(update).observe(track);
    update();
  }
  function selectedMedia(root) {
    const info = qs('[data-product-info]', root), gallery = qs('[data-gallery]', root);
    if (!info || !gallery || !gallery.goTo) return;
    const index = [...qs('[data-gallery-track]', gallery).children].findIndex(slide => slide.dataset.mediaId === info.dataset.featuredMedia);
    if (index >= 0) gallery.goTo(index, true);
  }
  function updateSticky(root) {
    const bar = qs('[data-sticky-buy]', root), info = qs('[data-product-info]', root);
    if (!bar || !info) return;
    const button = qs('[data-add-button]', info), stickyButton = qs('[data-sticky-add]', bar);
    stickyButton.disabled = button.disabled;
    stickyButton.textContent = button.textContent;
    const title = qs('h1', info).textContent;
    const variant = info.dataset.variantTitle;
    message(qs('[data-sticky-title]', bar), title + (variant && variant !== 'Default Title' ? ' · ' + variant : ''));
    message(qs('[data-sticky-price]', bar), qs('[data-product-price]', info).textContent);
  }
  let stickyFrame = 0;
  function refreshStickyVisibility() {
    cancelAnimationFrame(stickyFrame);
    stickyFrame = requestAnimationFrame(() => {
      let anyVisible = false;
      qsa('[data-product-root]').forEach(root => {
        const bar = qs('[data-sticky-buy]', root), button = qs('[data-add-button]', root);
        if (!bar || !button) return;
        const visible = button.getBoundingClientRect().bottom < 0 && matchMedia('(max-width: 999px)').matches;
        bar.hidden = !visible;
        anyVisible = anyVisible || visible;
      });
      document.body.classList.toggle('has-sticky-buy', anyVisible);
    });
  }
  function observeBuyButton(root) {
    updateSticky(root);
    refreshStickyVisibility();
  }
  window.addEventListener('scroll', refreshStickyVisibility, { passive: true });
  window.addEventListener('resize', refreshStickyVisibility);
  async function changeOptions(root, changedInput) {
    if (!root) return;
    controllers.get(root)?.abort();
    const controller = new AbortController();
    controllers.set(root, controller);
    const info = qs('[data-product-info]', root);
    const oldQuantity = Number(qs('[name=quantity]', info)?.value || 1);
    const selected = qsa('[data-option-value-id]:checked', info).map(input => input.dataset.optionValueId);
    const productUrl = changedInput?.dataset.productUrl || root.dataset.productUrl;
    const url = new URL(productUrl, location.origin);
    url.searchParams.delete('variant');
    url.searchParams.set('option_values', selected.join(','));
    url.searchParams.set('section_id', root.dataset.sectionId);
    const focusId = changedInput?.id;
    info.setAttribute('aria-busy', 'true');
    qs('[data-add-button]', info).disabled = true;
    qs('[name=id]', info).disabled = true;
    message(qs('[data-product-feedback]', info), strings.loading_options);
    updateSticky(root);
    const timer = setTimeout(() => controller.abort('timeout'), 15000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error('Options could not be loaded.');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextRoot = qs('[data-product-root]', doc), nextInfo = qs('[data-product-info]', doc);
      if (!nextRoot || !nextInfo || !qs('[data-product-form]', nextInfo)) throw new Error('Options could not be loaded.');
      if (controllers.get(root) !== controller) return;
      const qty = qs('[name=quantity]', nextInfo);
      const min = Number(qty.min || 1), step = Number(qty.step || 1), max = qty.max ? Number(qty.max) : Infinity;
      qty.value = Math.min(max, Math.max(min, min + Math.floor((oldQuantity - min) / step) * step));
      if (nextRoot.dataset.productUrl !== root.dataset.productUrl) {
        const nextVisuals = qs('[data-product-visuals]', nextRoot);
        qs('[data-product-visuals]', root).replaceWith(nextVisuals);
        initGallery(qs('[data-gallery]', root));
      }
      info.replaceWith(nextInfo);
      root.dataset.productUrl = nextRoot.dataset.productUrl;
      const destination = new URL(nextRoot.dataset.productUrl, location.origin);
      if (nextInfo.dataset.variantId) destination.searchParams.set('variant', nextInfo.dataset.variantId);
      else destination.searchParams.set('option_values', selected.join(','));
      history.replaceState({}, '', destination);
      observeBuyButton(root);
      selectedMedia(root);
      if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
    } catch (error) {
      if (controllers.get(root) !== controller) return;
      info.removeAttribute('aria-busy');
      const feedback = qs('[data-product-feedback]', info);
      message(feedback, strings.options_error, true);
      const retry = document.createElement('button'); retry.type = 'button'; retry.className = 'text-button'; retry.dataset.retryOptions = ''; retry.textContent = strings.retry; feedback.append(retry);
      // Leave purchase disabled until a fresh, confirmed variant is loaded.
    } finally { clearTimeout(timer); }
  }
  async function addProduct(form) {
    if (form.dataset.busy) return;
    const root = form.closest('[data-product-root]'), button = qs('[data-add-button]', form), feedback = qs('[data-product-feedback]', form);
    if (button.disabled || !form.reportValidity()) return;
    const data = new FormData(form);
    qsa('[data-option-value-id]', form).forEach(input => data.delete(input.name));
    form.dataset.busy = 'true';form.setAttribute('aria-busy', 'true');button.disabled = true;button.textContent = strings.adding;
    qsa('[data-option-value-id], [name=quantity], [data-quantity-step]', form).forEach(control => { control.disabled = true; });
    message(feedback, '');updateSticky(root);
    try {
      await request(endpoint('add'), { method: 'POST', headers: { Accept: 'application/json' }, body: data });
      message(feedback, strings.added);
      showNotice(strings.added);
      try { const cart = await request(config.root + 'cart.js');setCartCount(cart.item_count); } catch { /* The add succeeded; the cart page remains authoritative. */ }
    } catch (error) { message(feedback, error.message || strings.add_error, true); }
    finally {
      delete form.dataset.busy;form.removeAttribute('aria-busy');button.disabled = false;button.textContent = strings.add;
      qsa('[data-option-value-id], [name=quantity], [data-quantity-step]', form).forEach(control => { control.disabled = false; });updateSticky(root);
    }
  }
  function cartSectionFromResponse(data, sectionId) {
    if (!data.sections?.[sectionId]) return null;
    const doc = new DOMParser().parseFromString(data.sections[sectionId], 'text/html');
    return qs('[data-cart-root]', doc);
  }
  async function changeCart(line, quantity) {
    const root = line?.closest('[data-cart-root]');
    if (!root || cartBusy || !Number.isInteger(quantity) || quantity < 0) return;
    const input = qs('[data-cart-quantity]', line), previous = input.defaultValue;
    cartBusy = true;root.setAttribute('aria-busy', 'true');
    qsa('button,input', root).forEach(control => { control.disabled = true; });
    message(qs('[data-cart-feedback]', root), strings.updating_cart);
    try {
      const data = await request(endpoint('change'), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: line.dataset.lineKey, quantity, sections: [root.dataset.sectionId], sections_url: location.pathname }) });
      const next = cartSectionFromResponse(data, root.dataset.sectionId);
      setCartCount(data.item_count);
      if (!next) { location.assign(config.cart); return; }
      const lineIndex = qsa('[data-cart-line]', root).indexOf(line);
      root.replaceWith(next);
      const target = qsa('[data-cart-quantity]', next)[Math.min(lineIndex, data.items.length - 1)];
      if (target) target.focus({ preventScroll: true });
      message(qs('[data-cart-feedback]', next), quantity === 0 ? strings.removed : strings.cart_updated);
    } catch (error) {
      input.value = previous;
      root.removeAttribute('aria-busy');
      qsa('button,input', root).forEach(control => { control.disabled = false; });
      message(qs('[data-cart-feedback]', root), error.message || strings.change_error, true);
    } finally { cartBusy = false; }
  }
  function openContactFromHash(hash = location.hash) {
    const anchor = hash ? document.getElementById(hash.slice(1)) : null;
    if (hash === '#FooterContact' || anchor?.closest('#FooterContact')) {
      const details = document.getElementById('FooterContact');
      if (details) details.open = true;
    }
  }
  window.addEventListener('hashchange', () => openContactFromHash());
  const campaigns = new WeakMap();
  function initCampaigns(scope = document) {
    qsa('[data-campaign]', scope).forEach(section => {
      if (campaigns.has(section)) return;
      const video = qs('video', section), toggle = qs('[data-motion-toggle]', section);
      if (!video || !toggle) return;
      const poster = qs('.campaign-fallback', section), status = qs('[data-motion-status]', section);
      const motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
      const mobilePreference = matchMedia('(max-width: 699px)');
      let visible = false, manuallyPaused = false, failed = false, usingMobile = false;
      let attempt = 0;
      const initialSource = video.getAttribute('src');
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = true;
      video.controls = false;
      toggle.hidden = false;
      function updateButton() {
        const label = video.paused ? strings.play_film : strings.pause_film;
        qs('[data-motion-label]', section).textContent = label;
        toggle.setAttribute('aria-label', label);
        toggle.setAttribute('aria-pressed', String(!video.paused));
      }
      function pause() { attempt++; video.pause(); updateButton(); }
      async function play(userGesture = false) {
        if (failed) return;
        if (!userGesture && (!visible || document.hidden || manuallyPaused || section.dataset.autoplay !== 'true')) return;
        const currentAttempt = ++attempt;
        video.muted = true;
        try {
          await video.play();
          if (currentAttempt !== attempt) return;
          updateButton();
        } catch {
          // Autoplay can be denied by the browser; the manual Play action remains.
          if (currentAttempt !== attempt) return;
          if (userGesture && !failed) video.controls = true;
          updateButton();
        }
      }
      function chooseSource() {
        const useMobile = Boolean(section.dataset.mobileSrc) && mobilePreference.matches;
        const nextPoster = useMobile ? section.dataset.mobilePoster : section.dataset.desktopPoster;
        video.poster = nextPoster;
        poster.src = nextPoster;
        if (useMobile !== usingMobile) {
          pause();
          usingMobile = useMobile;
          if (useMobile) video.src = section.dataset.mobileSrc;
          else if (initialSource) video.src = initialSource;
          else video.removeAttribute('src');
          failed = false;
          toggle.disabled = false;
          toggle.hidden = false;
          video.hidden = false;
          poster.hidden = true;
          status.hidden = true;
          video.load();
          play();
        }
      }
      function visibility() { if (document.hidden) pause(); else play(); }
      function preferenceChanged() { if (motionPreference.matches) pause(); else play(); }
      toggle.addEventListener('click', () => {
        if (video.paused) { manuallyPaused = false; play(true); }
        else { manuallyPaused = true; pause(); }
      });
      video.addEventListener('play', updateButton);
      video.addEventListener('pause', updateButton);
      video.addEventListener('error', () => {
        failed = true;
        pause();
        video.hidden = true;
        poster.hidden = false;
        toggle.hidden = true;
        status.hidden = false;
        status.textContent = strings.film_unavailable;
      });
      chooseSource();
      updateButton();
      let observer;
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(entries => {
          visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.15;
          if (visible) play(); else pause();
        }, { threshold: [0, 0.15] });
        observer.observe(qs('.campaign-media', section));
      }
      document.addEventListener('visibilitychange', visibility);
      mobilePreference.addEventListener?.('change', chooseSource);
      motionPreference.addEventListener?.('change', preferenceChanged);
      campaigns.set(section, () => {
        pause(); observer?.disconnect();
        document.removeEventListener('visibilitychange', visibility);
        mobilePreference.removeEventListener?.('change', chooseSource);
        motionPreference.removeEventListener?.('change', preferenceChanged);
      });
    });
  }

  function initAddresses() {
    qsa('[data-address-fields]').forEach(fields => {
      if (fields.dataset.ready) return;
      fields.dataset.ready = 'true';
      const country = qs('[data-country-select]', fields), province = qs('[data-province-select]', fields);
      if (country.dataset.default) {
        const match = [...country.options].find(option => option.value === country.dataset.default || option.textContent === country.dataset.default);
        if (match) country.value = match.value;
      }
      function provinces(initial = false) {
        let options = [];
        try { options = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]'); } catch { /* Empty country selection. */ }
        province.replaceChildren(...options.map(([value, label]) => new Option(label, value)));
        province.disabled = !options.length;
        qs('[data-province-container]', fields).hidden = !options.length;
        if (initial && province.dataset.default) {
          const match = [...province.options].find(option => option.value === province.dataset.default || option.textContent === province.dataset.default);
          if (match) province.value = match.value;
        }
      }
      country.addEventListener('change', () => provinces());
      provinces(true);
    });
    qsa('.account-recover').forEach(details => { if (qs('.success-message, .errors', details)) details.open = true; });
  }
  function init(scope = document) {
    initNavigation();
    initAddresses();
    initCampaigns(scope);
    qsa('[data-gallery]', scope).forEach(initGallery);
    qsa('[data-product-root]', scope).forEach(root => { observeBuyButton(root); selectedMedia(root); });
  }
  document.addEventListener('change', event => {
    if (event.target.matches('[data-option-value-id]')) changeOptions(event.target.closest('[data-product-root]'), event.target);
    if (event.target.matches('[data-cart-quantity]')) { if (event.target.checkValidity()) changeCart(event.target.closest('[data-cart-line]'), Number(event.target.value)); else event.target.reportValidity(); }
  });
  document.addEventListener('click', event => {
    const zoom = event.target.closest('[data-image-zoom]');
    const viewer = qs('[data-image-viewer]');
    if (zoom && viewer && typeof viewer.showModal === 'function') {
      event.preventDefault();
      imageOpener = zoom;
      const image = qs('[data-zoom-image]', viewer);
      image.src = zoom.href;
      image.alt = qs('img', zoom)?.alt || '';
      viewer.showModal();
      qs('[data-close-image]', viewer).focus();
    }
    if (event.target.closest('[data-close-image]')) { viewer?.close(); imageOpener?.focus({preventScroll: true}); }
    if (event.target === viewer) { viewer.close(); imageOpener?.focus({preventScroll: true}); }
    const close = event.target.closest('[data-close-nav]');
    if (close) { const menu = close.closest('[data-nav]'); closeMenu();qs('summary', menu)?.focus(); }
    if (event.target.closest('[data-nav] a')) closeMenu();
    if (event.target.closest('a[href="#FooterContact"]')) openContactFromHash('#FooterContact');
    if (event.target.closest('[data-dismiss-notice]')) qs('#CartNotice').hidden = true;
    const stepper = event.target.closest('[data-quantity-step]');
    if (stepper) {
      const input = qs('input', stepper.closest('.quantity-control'));const delta = Number(stepper.dataset.quantityStep);
      if (delta > 0) input.stepUp(); else input.stepDown();
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const remove = event.target.closest('[data-remove-line]');
    if (remove) { event.preventDefault();if (!cartBusy) changeCart(remove.closest('[data-cart-line]'), 0); }
    const sticky = event.target.closest('[data-sticky-add]');
    if (sticky) qs('[data-product-form]', sticky.closest('[data-product-root]'))?.requestSubmit();
    const retry = event.target.closest('[data-retry-options]');
    if (retry) changeOptions(retry.closest('[data-product-root]'));
  });
  document.addEventListener('keydown', event => {
    const openMenu = qs('[data-nav][open]');if (!openMenu) return;
    if (event.key === 'Escape') { closeMenu();qs('summary', openMenu)?.focus(); }
    if (event.key === 'Tab') {
      const elements = qsa('a,button,summary', qs('.nav-panel', openMenu)).filter(el => el.getClientRects().length);
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === qs('summary', openMenu))) { event.preventDefault();last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault();first?.focus(); }
    }
  });
  document.addEventListener('submit', event => {
    if (event.target.matches('[data-confirm-delete]') && !window.confirm('Delete this address?')) event.preventDefault();
    if (event.target.matches('[data-product-form]')) { event.preventDefault();addProduct(event.target); }
    if (event.target.matches('[data-cart-form]') && cartBusy) event.preventDefault();
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    qsa('[data-campaign]', event.target).forEach(section => { campaigns.get(section)?.(); campaigns.delete(section); });
    closeMenu();document.body.classList.remove('has-sticky-buy');
  });
  window.addEventListener('pageshow', () => { document.body.classList.remove('menu-open');closeMenu(); });
  init();
  openContactFromHash();
  const contactResult = qs('#FooterContact .success-message, #FooterContact .error-message');
  if (contactResult) { qs('#FooterContact').open = true; contactResult.setAttribute('tabindex', '-1'); contactResult.focus(); }
})();
